import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isNative } from "@/lib/native/platform";

export function useNotifications() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return [];
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", u.user.id)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    let chan: any;
    let disposed = false;
    let userId: string | null = null;

    // P0-RT-01 resilience (native only). On web the realtime channel behaves exactly as before:
    // a single subscribe with no reconnect/poll machinery, so the website is unaffected.
    const resilient = isNative();
    let backoffMs = 1000; // exponential-backoff base, capped below
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let subscribeWatchdog: ReturnType<typeof setTimeout> | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const nativeDisposers: Array<() => void> = [];

    const clearReconnect = () => {
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };
    const clearWatchdog = () => {
      if (subscribeWatchdog) { clearTimeout(subscribeWatchdog); subscribeWatchdog = null; }
    };
    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };
    const startPolling = () => {
      // Fallback: if the channel never reaches SUBSCRIBED, refetch on an interval so the
      // badge/list still update. Stops as soon as the channel comes up.
      if (pollTimer) return;
      pollTimer = setInterval(() => {
        qc.invalidateQueries({ queryKey: ["notifications"] });
      }, 30000);
    };

    const teardownChannel = () => {
      clearWatchdog();
      if (chan) { supabase.removeChannel(chan); chan = null; }
    };

    const scheduleReconnect = () => {
      if (disposed || !resilient) return;
      clearReconnect();
      const delay = Math.min(backoffMs, 30000);
      reconnectTimer = setTimeout(() => {
        backoffMs = Math.min(backoffMs * 2, 30000);
        connect();
      }, delay);
    };

    const connect = () => {
      if (disposed || !userId) return;
      teardownChannel();
      chan = supabase
        .channel("notif-" + userId)
        .on("postgres_changes", { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` }, (p: any) => {
          qc.invalidateQueries({ queryKey: ["notifications"] });
          const n = p.new;
          toast(n.title, { description: n.body });
        })
        .subscribe((status: string) => {
          if (!resilient) return;
          if (status === "SUBSCRIBED") {
            // Healthy: reset backoff, drop watchdog + poll fallback.
            backoffMs = 1000;
            clearWatchdog();
            clearReconnect();
            stopPolling();
            // Catch anything missed while we were disconnected.
            qc.invalidateQueries({ queryKey: ["notifications"] });
          } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
            scheduleReconnect();
          }
        });

      // Poll fallback if not SUBSCRIBED within ~5s.
      if (resilient) {
        clearWatchdog();
        subscribeWatchdog = setTimeout(() => {
          startPolling();
          scheduleReconnect();
        }, 5000);
      }
    };

    (async () => {
      const { data: u } = await supabase.auth.getUser();
      if (disposed || !u.user) return;
      userId = u.user.id;
      connect();

      if (!resilient) return;

      // Re-subscribe when the network comes back online (@capacitor/network) and when the
      // app returns to the foreground (@capacitor/app resume). Guarded dynamic imports so the
      // web bundle never pulls these in.
      try {
        const { Network } = await import("@capacitor/network");
        const netSub = await Network.addListener("networkStatusChange", (s) => {
          if (s.connected) {
            backoffMs = 1000;
            connect();
          }
        });
        nativeDisposers.push(() => { void netSub.remove(); });
      } catch { /* plugin unavailable: degrade silently */ }

      try {
        const { App } = await import("@capacitor/app");
        const appSub = await App.addListener("resume", () => {
          backoffMs = 1000;
          qc.invalidateQueries({ queryKey: ["notifications"] });
          connect();
        });
        nativeDisposers.push(() => { void appSub.remove(); });
      } catch { /* plugin unavailable: degrade silently */ }
    })();

    return () => {
      disposed = true;
      clearReconnect();
      clearWatchdog();
      stopPolling();
      teardownChannel();
      for (const d of nativeDisposers) d();
    };
  }, [qc]);

  return query;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await supabase.from("notifications").update({ status: "read", read_at: new Date().toISOString() }).eq("id", id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enablePushNotifications(): Promise<{ ok: boolean; reason?: string }> {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return { ok: false, reason: "Push not supported in this browser" };
    }
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { ok: false, reason: "Permission denied" };

    const reg = await navigator.serviceWorker.register("/sw.js");
    await navigator.serviceWorker.ready;

    const { data: keyData, error: keyErr } = await supabase.functions.invoke("research-notify", {
      method: "GET" as any,
      body: undefined,
    });
    // invoke doesn't accept query; fetch directly
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/research-notify?action=public_key`;
    const r = await fetch(url, {
      headers: { Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
    });
    const { key } = await r.json();
    if (!key) return { ok: false, reason: "Server VAPID key missing" };

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }
    const json = sub.toJSON() as any;
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return { ok: false, reason: "Not signed in" };

    const { error } = await supabase.from("push_subscriptions").upsert({
      user_id: u.user.id,
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth_key: json.keys.auth,
      user_agent: navigator.userAgent,
    }, { onConflict: "endpoint" });
    if (error) return { ok: false, reason: error.message };
    return { ok: true };
  } catch (e: any) {
    return { ok: false, reason: e.message };
  }
}

export async function notifyResearch(args: {
  user_ids: string[];
  category: string;
  title: string;
  message?: string;
  link_url?: string;
  data?: any;
}) {
  return supabase.functions.invoke("research-notify", { body: args });
}
